import Enumerable from "linq";
import Loading from 'quasar/src/plugins/loading/Loading.js';
import { FileHelper } from "src/extensions/utils/FileHelper";
import { computed, defineComponent, nextTick, reactive, ref } from "vue";
import { onBeforeRouteUpdate, useRoute, useRouter } from "vue-router";
import { adminApi } from "~/api/admin/Api";
import { api } from "~/api/Api";
import { GetHanyoParam } from "~/api/Common/Commands";
import { FindFileItemCommand, FindFilesInfoCommand } from "~/api/Common/Commands/FindFilesInfoCommand";
import { GetFileItem, GetFilesCommand } from "~/api/Common/Commands/GetFilesCommand";
import { FindFilesInfoResult } from "~/api/data/Common/FindFIlesInfoResult";
import BreadcrumbList from "~/components/BreadcrumbList.vue";
import { MsgBox } from "~/components/MsgBox";
import { auth } from "~/core/Auth";
import { Log } from "~/extensions/utils";
import { isNullOrUndefined, MypageMessageFormat } from "~/extensions/utils/DefineFunction";
import { FileCode, FileFetchKb, HanyoKb, InformationTarget, PageID } from "~/extensions/utils/DefineTypes";
import { HanyoHelper } from "~/extensions/utils/HanyoHelper";
import { commonStore } from "~/store/CommonStore";
import { AdminInformationValidator } from "./Models/AdminInformationValidator";
import { AdminInformationViewModel } from "./Models/AdminInformationViewModel";
const log = Log.create("InformationsEdit");
export default defineComponent({
    components: {
        BreadcrumbList
    },
    name: "InformationEdit",
    async setup() {
        const pageName = commonStore.pageName(PageID.informationEdit);
        const top = PageID.top;
        const infoList = PageID.adminInformationList;
        const infoEdit = PageID.informationEdit;
        const infoData = ref(new AdminInformationViewModel());
        const route = useRoute();
        // URLパラメータにお知らせNoが含まれている かつ コピーフラグが含まれていない場合は更新ボタン（編集状態）
        const isUpdate = computed(() => !isNullOrUndefined(urlParamInformationNo) && isNullOrUndefined(urlParamIsCopy));
        // URLパラメータにお知らせNoが含まれている かつ コピーフラグがtrueの倍は登録ボタン（コピー作成状態）
        const isCopy = computed(() => !isNullOrUndefined(urlParamInformationNo) && !isNullOrUndefined(urlParamIsCopy) && urlParamIsCopy);
        // URLパラメータ無しの場合は登録ボタン（新規登録状態）
        const isCreate = computed(() => isNullOrUndefined(urlParamInformationNo) && isNullOrUndefined(urlParamIsCopy));
        // 閲覧のみ判定。支店社員かつ掲載対象が全契約者の場合は閲覧のみ
        const isCanViewOnly = computed(() => !auth.userInfo.isHeadquarters && (infoData.value.target === InformationTarget.All || infoData.value.target === InformationTarget.Contractor));
        const router = useRouter();
        let urlParamIsCopy = Boolean(route.query.isCopy === "true");
        let urlParamInformationNo = Number(route.query.informationNo);
        let isFileNotExists = ref(false);
        const userInfo = auth.userInfo;
        // 掲載対象プルダウン
        const publishKbnList = ref([]);
        const departmentList = ref([]);
        //メール通知時刻（時）
        const emailNotificationHours = ref([]);
        //メール通知時刻（分）
        const emailNotificationMinutes = ref([]);
        const fileNotFoundMessage = MypageMessageFormat(commonStore.messages.PE0003, { val: "ファイル" });
        const fileCountOverMessage = MypageMessageFormat(commonStore.messages.PV0010, { val: "10" });
        const fileSizeOverMessage = MypageMessageFormat(commonStore.messages.PV0009, { val: "ファイル", val1: "10" });
        const totalFileSizeOverMessage = MypageMessageFormat(commonStore.messages.PV0009, { val: "合計ファイル", val1: "100" });
        const findFilesInfoCommand = new FindFilesInfoCommand();
        const getFilesCommand = new GetFilesCommand();
        const fileInfoResult = ref(new FindFilesInfoResult());
        const zipFile = ref(null);
        // 利用規約同意済みバージョン
        let agreedTermsOfUseVersion = null;
        // 既存ファイルテーブルカラム
        const columns = [
            { name: "filename", align: "left", label: "ファイル名", field: "filename" },
            { name: "isDelete", align: "center", label: "削除", field: "isDelete" }
        ];
        const allowExtensions = ["jpeg", "jpg", "png", "pdf"];
        let valid = reactive(new AdminInformationValidator(infoData.value));
        const isTargetedForEmailNotification = (target) => {
            if (target === null) {
                return false;
            }
            return [InformationTarget.Property, InformationTarget.Contractor].includes(target);
        };
        const isEmailNotificationAllowed = () => {
            if (isCanViewOnly.value || infoData.value.isEmailSent) {
                return false;
            }
            return isTargetedForEmailNotification(infoData.value.target);
        };
        const isEmailNotificationDateTimeAllowed = () => {
            if (isCanViewOnly.value || infoData.value.isEmailSent) {
                return false;
            }
            return infoData.value.isEmailNotification && isTargetedForEmailNotification(infoData.value.target);
        };
        const getHanyo = async () => {
            let gethanyoParam = [
                new GetHanyoParam(HanyoKb.TargetType, auth.userInfo.isHeadquarters ? "" : "1", !auth.userInfo.isHeadquarters && !isUpdate.value ? "1" : ""),
                new GetHanyoParam(HanyoKb.EmailNotificationHour),
                new GetHanyoParam(HanyoKb.EmailNotificationMinute)
            ];
            let hanyoList = await HanyoHelper.getHanyoList(gethanyoParam);
            let publishKbn = hanyoList.GetByHanyoKb(HanyoKb.TargetType);
            if (userInfo.isHeadquarters) {
                publishKbnList.value = publishKbn;
            }
            else {
                publishKbnList.value = publishKbn.filter(x => [Number(InformationTarget.Department), Number(InformationTarget.Property), Number(InformationTarget.Contractor)].includes(x.value));
            }
            emailNotificationHours.value = hanyoList.GetByHanyoKb(HanyoKb.EmailNotificationHour);
            emailNotificationMinutes.value = hanyoList.GetByHanyoKb(HanyoKb.EmailNotificationMinute);
        };
        const initializeData = async () => {
            await getHanyo();
            const department = await api.common.getDepartmentList();
            departmentList.value = department;
            if (!(isUpdate.value || isCopy.value)) {
                return;
            }
            const informationsData = await adminApi.adminInfomation.getAdmin(urlParamInformationNo);
            if (!informationsData) {
                return;
            }
            infoData.value = new AdminInformationViewModel(informationsData);
            infoData.value.isCopy = isCopy.value;
            valid = reactive(new AdminInformationValidator(infoData.value));
            if (infoData.value.existingFiles.length > 0) {
                await findFilesInfo(infoData.value.informationNo);
            }
        };
        /**掲載対象変更時の処理 */
        const onChangeIsTarget = (target) => {
            if (InformationTarget.All === target || InformationTarget.Department === target || InformationTarget.Company === target) {
                resetMailNotificationSetting();
                resetResponceRequiredSetting();
            }
            resetTargets();
            valid.resetTo("nyukyokeiyakuNo");
            valid.resetTo("buknCd");
            if (InformationTarget.Department === target && !userInfo.isHeadquarters) {
                nextTick(() => {
                    infoData.value.departmentCode = userInfo.departmentCode;
                });
            }
        };
        /**メール通知チェックボックス変更時処理 */
        const onChangeIsMailNotification = () => {
            if (infoData.value.isEmailNotification) {
                return;
            }
            resetMailNotificationSetting();
        };
        /**要回答チェックボックス変更時処理 */
        const onChangeIsResponceRequired = () => {
            if (infoData.value.isResponseRequired) {
                return;
            }
            resetResponceRequiredSetting();
        };
        /**メール通知設定リセット */
        const resetMailNotificationSetting = () => {
            infoData.value.isEmailNotification = false;
            infoData.value.emailNotificationDate = "";
            infoData.value.emailNotificationHour = null;
            infoData.value.emailNotificationMinute = null;
        };
        /**要回答設定リセット */
        const resetResponceRequiredSetting = () => {
            infoData.value.isResponseRequired = false;
            infoData.value.responseDeadline = "";
            infoData.value.affirmativeText = "";
            infoData.value.negativeText = "";
            infoData.value.negativeNotificationDepartment = [];
        };
        const resetTargets = () => {
            infoData.value.nyukyokeiyakuNo = null;
            infoData.value.buknCd = null;
            infoData.value.departmentCode = null;
            infoData.value.keiyakusyaName = "";
            infoData.value.buknName = "";
        };
        const GetKeiyakusha = async (nyukyokeiyakuNo) => {
            if (nyukyokeiyakuNo === null) {
                return;
            }
            //未入力または9文字以上の場合は検索しない。
            if (isNullOrUndefined(nyukyokeiyakuNo) || nyukyokeiyakuNo.toString().length > 8) {
                return;
            }
            try {
                const result = await api.common.getKeiyakusha(nyukyokeiyakuNo);
                if (isNullOrUndefined(result)) {
                    valid.setTo("nyukyokeiyakuNo", MypageMessageFormat(commonStore.messages.PE0003, { val: "契約者" }));
                }
                else {
                    infoData.value.keiyakusyaName = result.userName;
                    infoData.value.buknCd = result.buknCd;
                    infoData.value.buknName = result.buknName;
                    infoData.value.departmentCode = result.eigyoshoCd;
                    agreedTermsOfUseVersion = result.agreedTermsOfUseVersion;
                    valid.resetTo("nyukyokeiyakuNo");
                    valid.errorTo("nyukyokeiyakuNo");
                }
            }
            catch (ex) {
                if (ex.status === 404) {
                    valid.setTo("nyukyokeiyakuNo", MypageMessageFormat(commonStore.messages.PE0003, { val: "契約者" }));
                }
                else {
                    valid.setTo("nyukyokeiyakuNo", ex.data.errorMessage);
                }
                log.error("GetKeiyakusha", ex);
            }
        };
        const GetBuken = async (buknCd) => {
            if (buknCd === null) {
                return;
            }
            //未入力または9文字以上の場合は検索しない。
            if (isNullOrUndefined(buknCd) || buknCd.toString().length > 8) {
                return;
            }
            try {
                const result = await api.common.getBuken(buknCd);
                if (isNullOrUndefined(result)) {
                    valid.setTo("buknCd", MypageMessageFormat(commonStore.messages.PE0003, { val: "物件" }));
                }
                else {
                    infoData.value.buknName = result.buknName;
                    infoData.value.departmentCode = result.eigyoshoCd;
                    valid.resetTo("buknCd");
                    valid.errorTo("buknCd");
                }
            }
            catch (ex) {
                if (ex.status === 404) {
                    valid.setTo("buknCd", MypageMessageFormat(commonStore.messages.PE0003, { val: "物件" }));
                }
                else {
                    valid.setTo("buknCd", ex.data.errorMessage);
                }
                log.error("GetBuken", ex);
            }
        };
        /** メール通知の活性判定*/
        const isActiveEmailSent = () => {
            return isEmailNotificationAllowed();
        };
        /** メール通知 日付 時間 分の活性判定 */
        const isActiveEmailDateTime = () => {
            return isEmailNotificationDateTimeAllowed();
        };
        const openPdfInNewTab = async (fileId) => {
            if (infoData.value.informationNo === null) {
                return;
            }
            Loading.show();
            try {
                // 引数fileIdと一致するファイル情報を取得
                const targetFileInfo = fileInfoResult.value.fileList.find((item) => item.fileId === fileId);
                if (!targetFileInfo) {
                    throw new Error("File Not Found");
                }
                getFilesCommand.informationNo = infoData.value.informationNo;
                getFilesCommand.fileList = [new GetFileItem(targetFileInfo.fileNo, FileFetchKb.Storage, targetFileInfo.fileCode, targetFileInfo.fileExists, targetFileInfo.fileId)];
                const result = await api.common.getFiles(getFilesCommand);
                // Zip内の最初のファイルを取得
                const fileData = await FileHelper.getFirstFileFromZip(result);
                if (!fileData) {
                    throw new Error("File Not Found in ZIP");
                }
                let mimeType = FileHelper.getMimeType(fileData.fileName);
                const displayFile = new Blob([fileData.blob], { type: mimeType });
                const imageUrl = FileHelper.createObjectUrl(displayFile);
                const pdfWindow = window.open("", "_blank");
                if (pdfWindow) {
                    pdfWindow.document.write(`
          <html>
            <head></head>
            <body style="height: 100%; width: 100%; overflow: hidden; margin:0px; background-color: rgb(82, 86, 89);">
              <iframe src="${imageUrl}" width="100%" height="100%" style="border:none;"></iframe>
            </body>
          </html>
        `);
                }
            }
            catch (ex) {
                log.error("openPdfInNewTab", ex);
            }
            finally {
                Loading.hide();
            }
        };
        /**ファイルの存在チェック */
        const filecheck = async () => {
            for await (let x of infoData.value.newFiles) {
                if (!isFileNotExists.value) {
                    await x.arrayBuffer().catch(() => {
                        isFileNotExists.value = true;
                    });
                }
            }
        };
        /**ファイル添付時の処理 */
        const onFilePicked = async (value) => {
            let files = value;
            let data = Enumerable.from(infoData.value.existingFiles)
                .where(x => !x.isDelete)
                .toArray();
            //ファイル数チェック
            if (files.length + data.length > 10) {
                await MsgBox.error(fileCountOverMessage);
                return;
            }
            let msg = "";
            let totalFileSize = 0;
            // eslint-disable-next-line @typescript-eslint/no-for-in-array
            for (let key in files) {
                let file = files[key];
                //ファイル拡張子チェック
                if (!FileHelper.hasAllowedExtension(file.name, allowExtensions)) {
                    msg = commonStore.messages.PV0008;
                    break;
                }
                //ファイルサイズが10MB以上の場合エラーとする
                if (file.size > 10485760) {
                    msg = fileSizeOverMessage;
                    break;
                }
                totalFileSize += file.size;
            }
            //全ファイルの合計サイズが100MBを超えている場合エラー
            if (totalFileSize > 104857600) {
                msg = totalFileSizeOverMessage;
            }
            if (msg !== "") {
                await MsgBox.error(msg);
                return;
            }
            infoData.value.newFiles = files;
        };
        /**新規登録処理 */
        const onCreate = async (termsOfUseVersionMsg) => {
            const isCreate = await MsgBox.confirm(termsOfUseVersionMsg ?? commonStore.messages.PI0001);
            if (isCreate) {
                await filecheck();
                if (isFileNotExists.value) {
                    await MsgBox.error(fileNotFoundMessage);
                    return;
                }
                try {
                    Loading.show();
                    await adminApi.adminInfomation.insertAdmin(infoData.value);
                    await MsgBox.info(commonStore.messages.PI0002);
                    await router.push({ name: "admin-information-list" });
                }
                catch (ex) {
                    log.error("create", ex);
                    await MsgBox.error(ex.data.errorMessage);
                    await router.push({ name: "admin-information-list" });
                }
                finally {
                    Loading.hide();
                }
            }
        };
        /**更新処理 */
        const onUpdate = async (termsOfUseVersionMsg) => {
            const isUpdate = await MsgBox.confirm(termsOfUseVersionMsg ?? commonStore.messages.PI0003);
            if (isUpdate) {
                await filecheck();
                if (isFileNotExists.value) {
                    await MsgBox.error(fileNotFoundMessage);
                    return;
                }
                try {
                    Loading.show();
                    await adminApi.adminInfomation.updateAdmin(infoData.value);
                    Loading.hide();
                    await MsgBox.info(commonStore.messages.PI0004);
                    router.push({ name: "admin-information-list" });
                }
                catch (ex) {
                    await MsgBox.error(ex.data.errorMessage);
                    log.error("update", ex);
                }
                finally {
                    Loading.hide();
                }
            }
        };
        /**削除処理 */
        const onDelete = async () => {
            const isDelete = await MsgBox.negativeConfirm(commonStore.messages.PI0005);
            if (isDelete) {
                try {
                    Loading.show();
                    await adminApi.adminInfomation.deleteAdmin(Number(infoData.value.informationNo));
                    Loading.hide();
                    await MsgBox.info(commonStore.messages.PI0006);
                    router.push({ name: "admin-information-list" });
                }
                catch (ex) {
                    await MsgBox.error(ex.data.errorMessage);
                    log.error("delete", ex);
                }
                finally {
                    Loading.hide();
                }
            }
        };
        /**登録or更新ボタン押下時の処理 */
        const onClick = async () => {
            if (valid.error) {
                return;
            }
            // ファイル名重複チェック(既存ファイルと添付ファイルで重複していないか)
            let othrFileNames = infoData.value.existingFiles.filter(x => !x.isDelete).map(x => x.fileName);
            let duplicatedFile = infoData.value.newFiles.filter(x => {
                return othrFileNames.includes(x.name);
            });
            if (duplicatedFile.length > 0) {
                await MsgBox.error(commonStore.messages.PV0011);
                return;
            }
            // ファイル名重複チェック(添付ファイル内でファイル名が重複していないか)
            let fileNameListInAttachment = infoData.value.newFiles.map(x => x.name);
            let hasDuplicate = new Set(fileNameListInAttachment).size !== fileNameListInAttachment.length;
            if (hasDuplicate) {
                await MsgBox.error(commonStore.messages.PV0011);
                return;
            }
            isFileNotExists.value = false;
            let termsOfUseVersionMsg = null;
            // お知らせ利用規約同意済みチェック
            if (infoData.value.target === InformationTarget.Contractor) {
                if (agreedTermsOfUseVersion == null) {
                    Loading.show();
                    await GetKeiyakusha(infoData.value.nyukyokeiyakuNo);
                    Loading.hide();
                }
                let informationTermsOfUseVersion = commonStore.informationTermsOfUseVersion;
                if (compareVersion(agreedTermsOfUseVersion, informationTermsOfUseVersion) < 0) {
                    termsOfUseVersionMsg = commonStore.messages.PI0020;
                }
            }
            if (isUpdate.value) {
                await onUpdate(termsOfUseVersionMsg);
            }
            else {
                await onCreate(termsOfUseVersionMsg);
            }
        };
        const compareVersion = (a, b) => {
            if (a === b) {
                return 0;
            }
            if (isNullOrUndefined(a) || isNullOrUndefined(b)) {
                return -1;
            }
            // バージョンを"."でユニットに切り分ける
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const aUnits = a.split(".");
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const bUnits = b.split(".");
            // ユニット数が少ない方の表記に探索幅を合わせる
            const len = Math.min(aUnits.length, bUnits.length);
            // 探索幅に従ってユニット毎に比較していく
            for (let i = 0; i < len; i++) {
                // A > B
                if (parseInt(aUnits[i], 10) > parseInt(bUnits[i], 10)) {
                    return 1;
                }
                // B > A
                if (parseInt(aUnits[i], 10) < parseInt(bUnits[i], 10)) {
                    return -1;
                }
            }
            // 個々のユニットが完全に一致している場合はユニット数が多い方が大きいとする
            if (aUnits.length > bUnits.length) {
                return 1;
            }
            if (aUnits.length < bUnits.length) {
                return -1;
            }
            // 上記の処理で判定仕切れない場合は同一とみなす
            return 0;
        };
        /**ファイル一覧の削除チェックボックス更新時の処理
         * 削除チェックボックスのチェックを外した場合のファイル数超過チェックを行う
         */
        const deleteCheck = async (index) => {
            let data = Enumerable.from(infoData.value.existingFiles)
                .where(x => !x.isDelete)
                .toArray();
            if (infoData.value.newFiles.length + data.length > 10) {
                infoData.value.existingFiles[index - 1].isDelete = !infoData.value.existingFiles[index - 1].isDelete;
                await MsgBox.error(fileCountOverMessage);
            }
        };
        const changeNyukyokeiyakuNo = () => {
            infoData.value.keiyakusyaName = "";
            infoData.value.buknCd = null;
            infoData.value.buknName = "";
            infoData.value.departmentCode = null;
            agreedTermsOfUseVersion = null;
            valid.resetTo("nyukyokeiyakuNo");
            valid.errorTo("nyukyokeiyakuNo");
        };
        const ChangeBuknCd = () => {
            if (infoData.value.target !== InformationTarget.Property) {
                return;
            }
            infoData.value.buknName = "";
            infoData.value.departmentCode = null;
            valid.resetTo("buknCd");
            valid.errorTo("buknCd");
        };
        /**ルートを書き換えた場合の処理 */
        onBeforeRouteUpdate(async (to, from) => {
            if (to.query.informationNo !== from.query.informationNo) {
                urlParamInformationNo = Number(to.query.informationNo);
                /**初期化処理 */
                try {
                    await initializeData();
                }
                catch (ex) {
                    await MsgBox.error(ex.data.errorMessage);
                    log.error("get", ex);
                }
            }
        });
        // ファイル情報取得
        const findFilesInfo = async (informationNo) => {
            // ファイル情報取得パラメータ設定
            findFilesInfoCommand.informationNo = informationNo;
            findFilesInfoCommand.fileList = [new FindFileItemCommand(FileFetchKb.Storage, FileCode.InformationFile)];
            fileInfoResult.value = await api.common.findFilesInfo(findFilesInfoCommand);
        };
        // ファイル情報取得
        const getFiles = async () => {
            try {
                getFilesCommand.informationNo = infoData.value.informationNo;
                getFilesCommand.fileList = fileInfoResult.value.fileList.map(item => {
                    return {
                        fileNo: item.fileNo,
                        fileId: item.fileId,
                        fileFetchKb: FileFetchKb.Storage,
                        fileCode: item.fileCode,
                        fileExists: item.fileExists
                    };
                });
                // ファイル取得
                const result = await api.common.getFiles(getFilesCommand);
                zipFile.value = result;
                const zipFiles = await FileHelper.getAllFilesFromZip(zipFile.value);
                return zipFiles.map(file => new File([file.blob], file.fileName, { type: file.blob.type }));
            }
            catch (ex) {
                log.error(ex);
                throw ex;
            }
        };
        /** 初期表示処理 */
        try {
            await initializeData();
            // コピー かつ 既存ファイルが存在する場合、各既存ファイルの本体情報を取得して添付ファイル情報にセットする
            if (isCopy.value && infoData.value.existingFiles) {
                // ファイル取得
                let files = await getFiles();
                infoData.value.newFiles.push(...files);
                // お知らせ登録時、既存ファイルと添付ファイルのファイル名重複チェックに引っかからないように既存ファイルの値はクリアする
                infoData.value.existingFiles.splice(0);
            }
        }
        catch (ex) {
            await MsgBox.error(ex.data.errorMessage);
            log.error("get", ex);
        }
        return {
            pageName,
            top,
            infoList,
            infoEdit,
            router,
            columns,
            infoData,
            valid,
            isUpdate,
            publishKbnList,
            emailNotificationHours,
            emailNotificationMinutes,
            departmentList,
            InformationTarget,
            files: ref(null),
            userInfo,
            isCanViewOnly,
            isCopy,
            GetKeiyakusha,
            GetBuken,
            isActiveEmailSent,
            isActiveEmailDateTime,
            openPdfInNewTab,
            onCreate,
            onUpdate,
            onDelete,
            onFilePicked,
            deleteCheck,
            filecheck,
            onClick,
            onChangeIsMailNotification,
            onChangeIsResponceRequired,
            onChangeIsTarget,
            changeNyukyokeiyakuNo,
            ChangeBuknCd
        };
    }
});
